import { activeEntries } from '@khata/core';
import { DateInput, Field, MoneyInput, TextInput } from '../components/index.js';
import { AccountPicker } from '../components/AccountPicker.js';
import { fromApi, isErr, money, useFields, useSave } from '../lib/form.js';
import { taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { entries, master, state, submit, today } from '../lib/khata.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function MoneySheet(props: { kind: 'expense' | 'drawing' }) {
  const m = master.value;
  const isExp = props.kind === 'expense';
  const accounts = [...m.accounts.values()].filter((a) => a.active);
  const previous = [
    ...new Set(
      activeEntries(state.value, entries.value)
        .filter((e) => e.type === props.kind)
        .map((e) => (e.type === 'expense' ? e.purpose : e.type === 'drawing' ? e.who : ''))
        .filter(Boolean),
    ),
  ].sort();
  const [f, set] = useFields({
    who: isExp ? '' : t('money_owner'),
    amount: '',
    accountId: accounts[0]?.id ?? '',
    date: today.value,
    note: '',
  });
  const [error, busy, save] = useSave();

  const onSave = () =>
    save(async () => {
      if (!f.who.trim())
        return { message: isExp ? t('money_whatForMissing') : t('money_whoMissing'), field: 'who' };
      const amount = money(f.amount, 'amount', { min: 1 });
      if (isErr(amount)) return amount;
      const input = isExp
        ? { date: f.date, purpose: f.who, amount, accountId: f.accountId, note: f.note }
        : { date: f.date, who: f.who, amount, accountId: f.accountId, note: f.note };
      const r = await submit(props.kind, input);
      if (!r.ok) return fromApi(r.error);
      return t(isExp ? 'money_expenseSaved' : 'money_drawingSaved', { amount: taka(amount) });
    });

  return (
    <Sheet
      title={isExp ? t('sheet_expense') : t('sheet_drawing')}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <Field label={isExp ? t('money_whatFor') : t('money_who')}>
        <TextInput
          value={f.who}
          onInput={(v) => set('who', v)}
          list="whos"
          placeholder={isExp ? t('money_forPlaceholder') : t('money_whoPlaceholder')}
          invalid={error?.field === 'who'}
          autoFocus
        />
        <datalist id="whos">
          {previous.map((w) => (
            <option key={w} value={w} />
          ))}
        </datalist>
      </Field>
      <div class="grid-2">
        <Field label={t('amountTaka')}>
          <MoneyInput
            value={f.amount}
            onInput={(v) => set('amount', v)}
            invalid={error?.field === 'amount'}
          />
        </Field>
        <Field label={t('sheet_from')}>
          <AccountPicker
            value={f.accountId}
            onChange={(v) => set('accountId', v)}
            accounts={accounts}
            testId="account-picker"
          />
        </Field>
      </div>
      <div class="grid-2">
        <Field label={t('date')}>
          <DateInput
            value={f.date}
            onInput={(v) => set('date', v)}
            invalid={error?.field === 'date'}
          />
        </Field>
        <Field label={t('note')}>
          <TextInput value={f.note} onInput={(v) => set('note', v)} placeholder={t('optional')} />
        </Field>
      </div>
      <FormErrorLine error={error} />
    </Sheet>
  );
}
